import ChevronLeftIcon from "@heroicons/react/24/outline/ChevronLeftIcon";
import ChevronRightIcon from "@heroicons/react/24/outline/ChevronRightIcon";
import React, { FC, ReactElement } from "react";
import { useState } from "react";
import { useLocation } from "react-router-dom";

import { Box, Divider, Drawer, Stack, SvgIcon } from "@mui/material";
import IconButton from "@mui/material/IconButton";

import { Scrollbar } from "../../components/scrollbar";
import { Item, useMenuItems } from "../../hooks/use-menu-items";
import { SideNavItem } from "./side-nav-item";

const SIDE_NAV_WIDTH = 270;
const SIDE_NAV_COLLAPSED_WIDTH = 73; // icon size + padding + border right
const TOP_NAV_HEIGHT = 64;

const renderItems = ({
  collapse = false,
  depth = 0,
  items,
  pathname,
}: {
  collapse: boolean;
  depth?: number;
  items: Item[];
  pathname?: string | null;
}): ReactElement[] =>
  items.reduce(
    (acc: ReactElement[], item) =>
      reduceChildRoutes({
        acc,
        collapse,
        depth,
        item,
        pathname,
      }),
    [],
  );

const reduceChildRoutes = ({
  acc,
  collapse,
  depth,
  item,
  pathname,
}: {
  acc: ReactElement[];
  collapse: boolean;
  depth: number;
  item: Item;
  pathname?: string | null;
}): Array<ReactElement> => {
  const checkPath = !!(item.path && pathname);
  const partialMatch = checkPath ? pathname.includes(item.path!) : false;
  const exactMatch = checkPath ? pathname === item.path : false;
  const openState = localStorage.getItem("SideNavOpen_" + item.title);
  const startOpen = !(openState === "false");

  if (item.items) {
    acc.push(
      <SideNavItem
        active={partialMatch}
        collapse={collapse}
        depth={depth}
        external={item.external}
        icon={item.icon}
        key={item.title}
        openImmediately={partialMatch || startOpen}
        path={item.path}
        title={item.title}
      >
        <Stack
          component="ul"
          spacing={0.5}
          sx={{
            listStyle: "none",
            m: 0,
            p: 0,
          }}
        >
          {renderItems({
            collapse,
            depth: depth + 1,
            items: item.items,
            pathname,
          })}
        </Stack>
      </SideNavItem>,
    );
  } else {
    acc.push(
      <SideNavItem
        active={exactMatch}
        collapse={collapse}
        depth={depth}
        external={item.external}
        icon={item.icon}
        key={item.title}
        path={item.path}
        title={item.title}
      />,
    );
  }

  return acc;
};

interface SideNavProps {
  onPin?: () => void;
  pinned?: boolean;
}

export const SideNav: FC<SideNavProps> = (props) => {
  const { onPin, pinned = false } = props;
  const { pathname } = useLocation();
  const [hovered, setHovered] = useState<boolean>(false);

  const collapse = !(pinned || hovered);
  const items = useMenuItems();

  return (
    <Drawer
      open
      variant="permanent"
      PaperProps={{
        onMouseEnter: () => {
          setHovered(true);
        },
        onMouseLeave: () => {
          setHovered(false);
        },
        sx: {
          backgroundColor: "background.default",
          height: `calc(100% - ${TOP_NAV_HEIGHT}px)`,
          overflowX: "hidden",
          top: TOP_NAV_HEIGHT,
          transition: "width 250ms ease-in-out",
          width: collapse ? SIDE_NAV_COLLAPSED_WIDTH : SIDE_NAV_WIDTH,
          zIndex: (theme) => theme.zIndex.appBar - 100,
        },
      }}
    >
      <Scrollbar
        sx={{
          height: "100%",
          overflowX: "hidden",
          "& .simplebar-content": {
            height: "100%",
          },
        }}
      >
        <Box
          component="nav"
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            p: 2,
          }}
        >
          <Box
            component="ul"
            sx={{
              flexGrow: 1,
              listStyle: "none",
              m: 0,
              p: 0,
            }}
          >
            {renderItems({
              collapse,
              depth: 0,
              items,
              pathname,
            })}
          </Box>
          <Divider />
          <Box sx={{ pt: 1 }}>
            <IconButton onClick={onPin}>
              <SvgIcon fontSize="small">
                {pinned ? <ChevronLeftIcon /> : <ChevronRightIcon />}
              </SvgIcon>
            </IconButton>
          </Box>
        </Box>
      </Scrollbar>
    </Drawer>
  );
};
